import { useCallback } from "react";
import { useApolloClient } from "@apollo/client";
import { toast } from "@jsq/ds/core";
import { getErrorMessage } from "~/utils/errors";
import {
  useSetDataVaultExtensionFieldValueMutation,
  type SetDataVaultExtensionFieldValueInput,
  type ExtensionFieldTuple,
  type ExtensionFieldValueObjectType,
  type SetDataVaultExtensionFieldValuePayload,
} from "../graphql";
import type { UseUpdateExtensionFieldValueReturn } from "../types";

/**
 * Updates an extension field value within an array of extension field tuples.
 * This utility function finds the matching extension field by ID and either updates
 * an existing value or adds a new value to the tuple.
 *
 * @param existingTuples - Array of extension field tuples to update
 * @param updatedExtensionFieldValue - The updated extension field value object
 * @returns Updated array of extension field tuples with the new/updated value
 *
 * @example
 * ```typescript
 * const updatedTuples = updateExtensionFieldInTuples(
 *   existingTuples,
 *   { id: "123", extensionField: { id: "field-1" }, value: "new value" }
 * );
 * ```
 */
export function updateExtensionFieldInTuples(
  existingTuples: ExtensionFieldTuple[] | null | undefined,
  updatedExtensionFieldValue: ExtensionFieldValueObjectType,
): ExtensionFieldTuple[] {
  if (!existingTuples || !updatedExtensionFieldValue.extensionField?.id) {
    return existingTuples || [];
  }

  const targetFieldId = updatedExtensionFieldValue.extensionField.id;

  const result = existingTuples.map((tuple) => {
    if (tuple?.extensionField?.id === targetFieldId) {
      const updatedValues = tuple.values
        ? tuple.values.map((value: ExtensionFieldValueObjectType) => {
            if (value?.id === updatedExtensionFieldValue.id) {
              return updatedExtensionFieldValue;
            }
            return value;
          })
        : [updatedExtensionFieldValue];

      const hasExistingValue = tuple.values?.some(
        (value: ExtensionFieldValueObjectType) => value?.id === updatedExtensionFieldValue.id,
      );

      if (!hasExistingValue) {
        updatedValues.push(updatedExtensionFieldValue);
      }

      return {
        ...tuple,
        values: updatedValues,
      };
    }

    return tuple;
  });

  return result;
}

/**
 * Configuration options for the useUpdateExtensionFieldValue hook.
 */
interface UseUpdateExtensionFieldValueOptions {
  /** Callback function called when the mutation succeeds */
  onSuccess?: (data: SetDataVaultExtensionFieldValuePayload | null | undefined) => void;
  /** Callback function called when the mutation fails */
  onError?: (error: Error) => void;
  /** Whether to invalidate Apollo cache after successful update. Defaults to true for modal submissions, should be false for inline editing. */
  invalidateCache?: boolean;
}

/**
 * Hook for updating individual Data Vault extension field values.
 * Provides functionality to update a single extension field value for an entity
 * with loading states, error handling, and success/error callbacks.
 *
 * @param entityId - The ID of the entity to update (can be overridden in updateValue params)
 * @param options - Configuration options including success and error callbacks
 * @returns Object containing updateValue function and mutation state
 *
 * @example
 * ```typescript
 * const { updateValue, loading, error } = useUpdateExtensionFieldValue(123, {
 *   onSuccess: (data) => console.log('Updated successfully', data),
 *   onError: (error) => console.error('Update failed', error)
 * });
 *
 * // Update a field value
 * await updateValue({
 *   entityId: 123,
 *   extensionFieldId: 'field-1',
 *   value: 'new value'
 * });
 * ```
 */
export const useUpdateExtensionFieldValue = (
  entityId: number | null,
  options: UseUpdateExtensionFieldValueOptions = {},
): UseUpdateExtensionFieldValueReturn => {
  const { onSuccess, onError, invalidateCache = true } = options;
  const apolloClient = useApolloClient();

  const [setDataVaultExtensionFieldValueMutation, { loading, error, reset }] =
    useSetDataVaultExtensionFieldValueMutation({
      errorPolicy: "all",
    });

  /**
   * Updates a single extension field value for an entity.
   *
   * @param params - Update parameters
   * @param params.entityId - The entity ID (overrides hook's entityId if provided)
   * @param params.extensionFieldId - The ID of the extension field to update
   * @param params.value - The new value to set
   * @returns Promise resolving to the mutation result
   * @throws Error if entityId is missing or mutation fails
   */
  const updateValue = useCallback(
    async (params: { entityId: number; extensionFieldId: string; value: string }) => {
      const targetEntityId = params.entityId || entityId;
      if (!targetEntityId) {
        const error = new Error("Entity ID is required");
        if (onError) onError(error);
        toast.error(`Update failed: ${error.message}`);
        throw error;
      }

      try {
        const input: SetDataVaultExtensionFieldValueInput = {
          entityId: targetEntityId,
          extensionFieldId: params.extensionFieldId,
          value: params.value,
        };

        const result = await setDataVaultExtensionFieldValueMutation({
          variables: { input },
        });

        if (result.data?.setDataVaultExtensionFieldValue?.errors?.length) {
          const errorMessages = result.data.setDataVaultExtensionFieldValue.errors
            .filter(Boolean)
            .join(", ");
          throw new Error(errorMessages || "Unknown error occurred");
        }

        const mutationResult = result.data?.setDataVaultExtensionFieldValue;

        // Conditionally invalidate Apollo cache for entity queries
        // Only invalidate for modal submissions, not inline editing
        if (invalidateCache) {
          apolloClient.cache.evict({
            fieldName: "investmentEntitiesByArena",
          });
          apolloClient.cache.evict({
            fieldName: "dataVaultExtensionFields",
          });
          apolloClient.cache.gc();
        }

        if (onSuccess && mutationResult) {
          onSuccess(mutationResult as SetDataVaultExtensionFieldValuePayload);
        }

        toast.success("Extension field value updated successfully");
        return mutationResult;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(getErrorMessage(err));
        if (onError) onError(error);
        toast.error(`Failed to update field value: ${error.message}`);
        throw error;
      }
    },
    [entityId, setDataVaultExtensionFieldValueMutation, apolloClient, onSuccess, onError],
  );

  return {
    updateValue,
    loading,
    error,
    reset,
  };
};

/**
 * Hook for batch updating multiple Data Vault extension field values.
 * Processes multiple field updates sequentially for a single entity,
 * providing consolidated error handling and progress feedback.
 *
 * @param entityId - The ID of the entity to update (required for batch operations)
 * @param options - Configuration options including success and error callbacks
 * @returns Object containing batchUpdate function and mutation state
 *
 * @example
 * ```typescript
 * const { batchUpdate, loading } = useBatchUpdateExtensionFieldValues(123, {
 *   onSuccess: (data) => console.log('Field updated', data),
 *   onError: (error) => console.error('Field update failed', error)
 * });
 *
 * // Update multiple fields
 * const { results, errors } = await batchUpdate([
 *   { extensionFieldId: 'field-1', value: 'value1' },
 *   { extensionFieldId: 'field-2', value: 'value2' }
 * ]);
 * ```
 */
export const useBatchUpdateExtensionFieldValues = (
  entityId: number | null,
  options: UseUpdateExtensionFieldValueOptions = {},
) => {
  const { onSuccess, onError, invalidateCache = true } = options;
  const apolloClient = useApolloClient();

  const [setDataVaultExtensionFieldValueMutation, { loading, error, reset }] =
    useSetDataVaultExtensionFieldValueMutation({
      errorPolicy: "all",
    });

  /**
   * Performs batch update of multiple extension field values for the entity.
   * Updates are processed sequentially to avoid overwhelming the server.
   *
   * @param updates - Array of field updates to perform
   * @param updates[].extensionFieldId - The ID of the extension field to update
   * @param updates[].value - The new value to set
   * @returns Promise resolving to object with results and errors arrays
   * @throws Error if entityId is null
   */
  const batchUpdate = useCallback(
    async (
      updates: Array<{
        extensionFieldId: string;
        value: string;
      }>,
    ) => {
      if (!entityId) {
        throw new Error("Entity ID is required for batch update");
      }

      const results = [];
      const errors = [];

      // Process updates sequentially to avoid overwhelming the server
      for (const update of updates) {
        try {
          const input: SetDataVaultExtensionFieldValueInput = {
            entityId,
            extensionFieldId: update.extensionFieldId,
            value: update.value,
          };

          const result = await setDataVaultExtensionFieldValueMutation({
            variables: { input },
          });

          if (result.data?.setDataVaultExtensionFieldValue?.errors?.length) {
            const errorMessages = result.data.setDataVaultExtensionFieldValue.errors
              .filter(Boolean)
              .join(", ");
            throw new Error(errorMessages || "Unknown error occurred");
          }

          const mutationResult = result.data?.setDataVaultExtensionFieldValue;
          results.push(mutationResult);

          // Call individual success callback if provided
          if (onSuccess && mutationResult) {
            onSuccess(mutationResult as SetDataVaultExtensionFieldValuePayload);
          }
        } catch (err) {
          const error = err instanceof Error ? err : new Error(getErrorMessage(err));
          errors.push({
            extensionFieldId: update.extensionFieldId,
            error: error.message,
          });

          // Call individual error callback if provided
          if (onError) {
            onError(error);
          }
        }
      }

      // Conditionally invalidate Apollo cache for entity queries
      // Only invalidate for modal submissions, not inline editing
      if (results.length > 0 && invalidateCache) {
        // Invalidate all queries that might contain entity extension field data
        apolloClient.cache.evict({
          fieldName: "investmentEntitiesByArena",
        });
        // Trigger garbage collection to remove evicted cache entries
        apolloClient.cache.gc();
      }

      // Show batch-specific toast messages (no individual toasts to avoid spam)
      if (errors.length === 0) {
        toast.success(`Successfully updated ${results.length} field(s)`);
      } else if (results.length === 0) {
        toast.error(`Failed to update all ${updates.length} field(s)`);
      } else {
        toast.warning(
          `Updated ${results.length} field(s), failed to update ${errors.length} field(s)`,
        );
      }

      return { results, errors };
    },
    [entityId, setDataVaultExtensionFieldValueMutation, apolloClient, onSuccess, onError],
  );

  return {
    batchUpdate,
    loading,
    error,
    reset,
  };
};
